import type { AiShieldFlagWire } from '../routes/ai-shield-flags.js'
import type { TrustResult } from './trust.js'
import { getAiShieldFlags } from './api.js'
import { clear, el } from './dom.js'
import { aiShieldIcon } from './icons.js'
import { getLang, t } from './labels.js'
import { openSheet } from './sheet.js'

// The AI-Shield DRILL-DOWN sheet — the per-event view behind the activity section's aggregate rows
// (plan: .claude/plans/ai-shield-drilldown.md). Tapping a category row opens this sheet filtered to that
// category; tapping the total opens it unfiltered. Split like its siblings: planAiShieldFlags() is the
// PURE shaping of the wire page into display rows (unit-tested); the render/orchestration below is the
// thin DOM + fetch transport (live-verify only). Register layout, like the section it explains: who,
// when, category, confidence — plus the "what": the CAPPED excerpt shadow.ts persists (quoted muted
// line, only when the flag carries one — pre-excerpt rows don't) and the deep link to the message
// in-chat (only when the flag carries a pointer). The full text was never stored.

export interface AiFlagRowModel {
  /** Resolved display name; null → the renderer falls back to the id (or the erased label if that's null too). */
  name: string | null
  /** null = the target was GDPR-erased — labelled, never shown as an id. */
  targetId: string | null
  /** The flag's timestamp, formatted for the active locale (e.g. "3 Jul, 14:22"). */
  atText: string
  category: string
  /** Rounded percent 0–100; null when the persisted verdict was malformed (rendered as an em dash). */
  confidencePct: number | null
  messageLink: string | null
  /** The capped excerpt of the flagged message; null = none persisted (pre-excerpt rows, erasure). */
  excerpt: string | null
}

/**
 * PURE: shape one wire page into display rows. Locale is a parameter (not read from labels) so the
 * shaping is deterministic under test; an unparsable timestamp falls back to the raw string — visible
 * and ugly beats silently dropped (the row still counts toward what the aggregates promised).
 */
export function planAiShieldFlags(flags: AiShieldFlagWire[], locale: string): AiFlagRowModel[] {
  const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  return flags.map((f) => {
    const at = new Date(f.at)
    return {
      name: f.targetName,
      targetId: f.targetId,
      atText: Number.isNaN(at.getTime()) ? f.at : fmt.format(at),
      category: f.category,
      confidencePct: f.confidence === null ? null : Math.round(f.confidence * 100),
      messageLink: f.messageLink,
      // Defensive read (like the service's): a server rolled back to the pre-excerpt route omits the
      // key entirely — `undefined` must fold to null here or the renderer quotes the word "undefined".
      excerpt: typeof f.excerpt === 'string' ? f.excerpt : null,
    }
  })
}

/**
 * What the row's trust control shows. 'absent' = there is no id to act on (a GDPR-erased or anonymous
 * target): the button is not rendered at all — we must not offer an action on a user we deliberately no
 * longer know, and re-identifying them to build one would defeat the erasure.
 */
export type TrustState = 'absent' | 'trust' | 'untrust'

/** PURE: the control's state for one row, given the trusted list as the SPA currently knows it. */
export function trustState(targetId: string | null, trustedIds: readonly string[]): TrustState {
  if (targetId === null)
    return 'absent'
  return trustedIds.includes(targetId) ? 'untrust' : 'trust'
}

/**
 * The trust control's wiring, injected by main.ts (which owns `current` and the API transport). `ids()` is
 * read at PAINT time — the display state; the write path (`set`) re-reads server-side before writing, so a
 * stale `ids()` can only mis-paint a label for a moment, never corrupt the stored list (trust.ts).
 */
export interface TrustControl {
  /** The trusted ids as the SPA last knows them. */
  ids: () => readonly string[]
  /** Add/remove the id. Resolves with the outcome; REJECTS on transport/API failure (shown in-sheet). */
  set: (targetId: string, next: boolean) => Promise<TrustResult>
}

interface RowDeps {
  /** Category is shown only in the unfiltered view — a filtered sheet already says it in the title. */
  showCategory: boolean
  openLink?: (url: string) => void
  trust?: TrustControl
  /**
   * Register this row's refresher, and refresh EVERY registered row. One person can hold many events in
   * the list (the audit found 21 flags on a single bot), so after one toggle every row must restyle —
   * otherwise the sheet shows "trust" and "remove from trusted" for the same person at once, and it also
   * leaves a confirmation open whose sentence has just become false.
   */
  register: (refresh: () => void) => void
  refreshAll: () => void
}

/**
 * The per-row trust control: an ACTION BAR of its own under the event (not a word buried in the meta
 * line — an action nobody notices is an action nobody uses; operator's rule, 2026-07-30), plus the zone
 * where the confirmation and any error land. Returns null when there is nothing to act on.
 *
 * The chip says what it DOES in full ("Trust this person"), never a bare verb, and when the person is
 * already exempt the bar states that first — the state is text, not a colour someone has to decode.
 *
 * ADD asks for confirmation and the confirmation NAMES the three defenses it opens (AI Shield, anti-flood,
 * content filters) — a generic "are you sure?" would hide exactly the part that matters. REMOVE is
 * restrictive, so one tap is enough. Nothing is painted optimistically: the label only changes after the
 * server-confirmed answer is in.
 */
function trustControlEl(m: AiFlagRowModel, zone: HTMLElement, deps: RowDeps): HTMLElement | null {
  const trust = deps.trust
  const id = m.targetId
  if (!trust || id === null)
    return null

  const bar = el('div', { class: 'flag-trust-bar' })
  const badge = el('span', { class: 'flag-trusted' }, t('ui-aishield-flags-trusted-badge'))
  const btn = el('button', { class: 'flag-trust', type: 'button' }) as HTMLButtonElement
  bar.append(badge, btn)
  let busy = false

  // The person as the operator sees them — a NAME, never the id (the id is a wire detail; showing it here
  // would re-introduce exactly the friction this control exists to remove). Unresolvable name → a neutral
  // label, not the number.
  const person = m.name ?? t('ui-aishield-flags-someone')

  // The label lookups below are deliberately LITERAL — never a variable or a ternary INSIDE the call: the
  // label gate (labels.gen.test.ts) scans for literal-argument call sites only, so a computed key would
  // ship unchecked and a typo would surface as a blank button on the operator's phone, not as a red test.
  // (The scan is a plain regex over the source — it reads comments too, so this note keeps none itself.)
  function paint(): void {
    const state = trustState(id, trust!.ids())
    btn.textContent = state === 'untrust' ? t('ui-aishield-flags-untrust') : t('ui-aishield-flags-trust')
    btn.classList.toggle('on', state === 'untrust')
    // The badge states the CURRENT state in words before the action offers to change it.
    badge.toggleAttribute('hidden', state !== 'untrust')
    btn.disabled = busy
  }

  /**
   * Restyle the button AND drop a pending confirmation — its sentence ("<name> will no longer be checked
   * by …") can have become false in the meantime, since the same person recurs across rows. An ERROR line
   * must survive a refresh, so only a confirmation block is cleared, never the whole zone blindly.
   */
  function refresh(): void {
    if (zone.querySelector('.flag-actions'))
      clear(zone)
    paint()
  }
  deps.register(refresh)

  function fail(message: string): void {
    clear(zone)
    zone.append(el('div', { class: 'flag-meta stats-error' }, message))
  }

  async function run(next: boolean): Promise<void> {
    if (busy)
      return // re-entrancy guard: a double-tap must not fire two read-modify-writes
    busy = true
    clear(zone) // takes the confirmation's buttons — and the focus that sits on one — off the DOM…
    btn.focus() // …so focus returns to the control that started this, never escaping the sheet
    paint() // disables the button while in flight — the pending state IS the disabled control
    try {
      const outcome = await trust!.set(id!, next)
      busy = false
      // 'noop' refreshes too: the re-read updated the SPA's state, and the truth may have moved.
      deps.refreshAll() // BEFORE fail() — a refresh drops confirmations, and would drop the message below
      if (outcome === 'full')
        fail(t('ui-aishield-flags-trust-full'))
    }
    catch {
      // Loud, in the sheet, where the operator is looking. `busy` is cleared BEFORE the refresh so the
      // button comes back enabled and the action can be retried in place (no reload needed).
      busy = false
      deps.refreshAll()
      fail(t('ui-aishield-flags-trust-error'))
    }
  }

  btn.addEventListener('click', () => {
    if (busy)
      return
    if (trustState(id, trust.ids()) === 'untrust') {
      void run(false) // removal is restrictive: no confirmation
      return
    }
    clear(zone)
    const warn = el('div', { class: 'flag-meta' }, el('b', {}, person), ' ', t('ui-aishield-flags-trust-warn'))
    const yes = el('button', { class: 'flag-trust on', type: 'button' }, t('ui-aishield-flags-trust-yes'))
    yes.addEventListener('click', () => void run(true))
    const no = el('button', { class: 'flag-trust', type: 'button' }, t('ui-aishield-flags-trust-no'))
    no.addEventListener('click', () => clear(zone))
    zone.append(warn, el('div', { class: 'flag-actions' }, yes, no))
    yes.focus() // the sheet's Tab trap queries live nodes, so the new buttons join it automatically
  })

  paint()
  return bar
}

// One event as a register entry: WHO + confidence on the main line; when · category · the message deep
// link · the trust button on the muted meta line (category only in the unfiltered view — a filtered sheet
// already says it in the title); the quoted excerpt on a third muted line, only when the flag carries one;
// last, the (initially empty) confirmation/error zone the trust control writes into. All text — the
// excerpt is HOSTILE user content — lands via el() text nodes (XSS-safe by construction).
function flagRowEl(m: AiFlagRowModel, deps: RowDeps): HTMLElement {
  const who = m.name ?? (m.targetId !== null ? `ID ${m.targetId}` : t('ui-aishield-flags-erased'))
  const main = el('div', { class: 'row' }, el('span', { class: 'label' }, who), el('span', { class: 'value' }, m.confidencePct === null ? '—' : `${m.confidencePct}%`))
  const meta: (string | Node)[] = [m.atText]
  if (deps.showCategory)
    meta.push(` · ${t(`ai-cat-${m.category}`)}`)
  if (m.messageLink !== null) {
    meta.push(' · ')
    // target=_blank is the un-wired fallback (plain browser tab); inside Telegram the SDK opener keeps
    // the Mini App alive and jumps to the message (same pattern as the footer site link, main.ts).
    const link = el('a', { class: 'flag-link', href: m.messageLink, target: '_blank', rel: 'noopener' }, t('ui-aishield-flags-open'))
    if (deps.openLink) {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        deps.openLink!(m.messageLink!)
      })
    }
    meta.push(link)
  }
  const parts: Node[] = [main, el('div', { class: 'flag-meta' }, ...meta)]
  if (m.excerpt !== null)
    parts.push(el('div', { class: 'flag-excerpt' }, `“${m.excerpt}”`))
  // The action bar goes LAST, on its own line: an action mixed into the metadata text reads as prose and
  // gets skipped. The confirmation/error zone follows it, so the answer appears where the tap happened.
  const zone = el('div', { class: 'flag-zone' })
  const trustBar = trustControlEl(m, zone, deps)
  if (trustBar)
    parts.push(trustBar)
  parts.push(zone)
  return el('div', { class: 'flag-row' }, ...parts)
}

export interface OpenAiShieldFlagsOpts {
  /** The bound chat id (display/scoping only — the server re-authorizes every request). */
  chat: string
  /** A SPAM_CATEGORIES token to filter by, or null for the unfiltered (total) view. */
  category: string | null
  /** The tapped row — focus returns to it on close (the shared sheet contract). */
  triggerEl?: HTMLElement
  /** Telegram's t.me opener (openTelegramLink), when the client has one. */
  openLink?: (url: string) => void
  /**
   * The trusted-list control (main.ts). Omitted → no trust button at all: the drill-down stays a
   * read-only view, which is what it was before this control existed.
   */
  trust?: TrustControl
  /**
   * Fires once when this sheet closes, on EVERY path — main.ts reopens the parent activity sheet here so
   * the ×/backdrop/Escape/Back steps ONE level back to the list, not all the way to the page.
   */
  onClose?: () => void
}

/**
 * Open the drill-down sheet and load the first page. Pages append under a "load more" button while the
 * server reports more (nextCursor); a first-load failure is a loud in-sheet error line (close + reopen
 * retries, like the sections); a load-more failure keeps the button so the admin can retry in place.
 * `opts.onClose` (main.ts) reopens the parent activity sheet so a dismissal goes ONE level back, not out.
 */
export function openAiShieldFlags(opts: OpenAiShieldFlagsOpts): void {
  const title = opts.category === null
    ? t('ui-aishield-results-title')
    : `${t('ui-aishield-results-title')} — ${t(`ai-cat-${opts.category}`)}`
  const list = el('div', { class: 'flag-list' })
  const footer = el('div', { class: 'flag-footer' })
  openSheet(aiShieldIcon('sheet-icon'), title, [
    el('p', { class: 'sheet-body' }, t('ui-aishield-flags-caption')),
    list,
    footer,
  ], { bodyKey: 'ui-aishield-flags-help' }, opts.triggerEl, opts.onClose)

  let cursor: string | null = null
  let loading = false
  // Every rendered row's trust refresher. One toggle restyles them ALL (the same person recurs across
  // pages), and the list only grows — pages append, rows are never removed while the sheet is open.
  const refreshers: (() => void)[] = []
  const rowDeps: Omit<RowDeps, 'showCategory'> = {
    register: (refresh: () => void) => refreshers.push(refresh),
    refreshAll: () => { for (const refresh of refreshers) refresh() },
    ...(opts.openLink === undefined ? {} : { openLink: opts.openLink }),
    ...(opts.trust === undefined ? {} : { trust: opts.trust }),
  }

  // ONE definition of the continuation button — the success path ("load more") and the failed-page path
  // ("retry") must stay the same control, or a change to one silently diverges from the other.
  function moreButton(): HTMLElement {
    const btn = el('button', { class: 'lc-btn flag-more', type: 'button' }, t('ui-aishield-flags-more'))
    btn.addEventListener('click', () => void loadPage())
    return btn
  }

  async function loadPage(): Promise<void> {
    if (loading)
      return // re-entrancy guard: a double-tap on "load more" must not fetch the same page twice
    loading = true
    clear(footer)
    footer.append(el('div', { class: 'flag-meta' }, '…')) // language-neutral pending, like the section skeleton
    try {
      const page = await getAiShieldFlags(opts.chat, {
        ...(opts.category === null ? {} : { category: opts.category }),
        ...(cursor === null ? {} : { cursor }),
      })
      const firstLoad = cursor === null
      cursor = page.nextCursor
      for (const m of planAiShieldFlags(page.flags, getLang()))
        list.append(flagRowEl(m, { ...rowDeps, showCategory: opts.category === null }))
      clear(footer)
      if (firstLoad && page.flags.length === 0) {
        // Reachable only when the aggregates went stale between paint and tap (rare) — an honest empty.
        list.append(el('div', { class: 'flag-meta' }, t('ui-aishield-results-empty')))
      }
      else if (cursor !== null) {
        footer.append(moreButton())
      }
    }
    catch {
      // Loud in-sheet, scoped: the sheet shows the failure where the admin is looking; the code carries
      // no extra detail worth routing (same stance as the sections' in-place errors). A failed LATER page
      // keeps the button (retry in place); a failed FIRST load has no cursor — close + reopen retries.
      clear(footer)
      footer.append(el('div', { class: 'flag-meta stats-error' }, t('ui-aishield-flags-error')))
      if (cursor !== null)
        footer.append(moreButton())
    }
    finally {
      loading = false
    }
  }

  void loadPage()
}
